import type { Bet, BetEvent, BoardProperty, IBoard, Outcomes } from '../../@types/Board.js';
import type { ITSPayloadBoard } from '../../@types/ITSPayload.js';
import type { Product } from '../../@types/Product.js';
import { getProductConfig, type ProductConfig } from '../../config/getProductConfig.js';
import { WagerError } from '../../error/WagerError.js';
import { bitmapToPositions } from '../../utils/bitwise.js';

const asArray = (input: number | Outcomes): Outcomes => (typeof input === 'number' ? bitmapToPositions(input) : input);

abstract class Board<TBoardSystemType extends string> implements IBoard<TBoardSystemType> {
  private readonly _boardProperties = new Set<BoardProperty>();

  private readonly _outcomes: Bet = [];

  private _systemNumber = 0;

  private readonly _product: Product;

  constructor(product: Product) {
    this._product = product;
  }

  get outcomes(): Readonly<Bet> {
    return Object.freeze(this._outcomes);
  }

  get isQuickPick(): boolean {
    const quickPicks = new Set<BoardProperty>(['WEIGHTEDQP', 'MODIFIEDQP', 'PARTIALQP']);
    return this.boardProperties.some((prop) => quickPicks.has(prop));
  }

  get boardProperties(): BoardProperty[] {
    return [...this._boardProperties] as const;
  }

  get product(): Product {
    return this._product;
  }

  get systemNumber(): number {
    return this._systemNumber;
  }

  get productConfig(): ProductConfig {
    return getProductConfig(this.product);
  }

  abstract get systemType(): TBoardSystemType;

  /**
   * Adds a bet selection of outcomes to the bet.
   * The format of the outcome selections is generic so if the format doesn't suite the composition of the wager it should be handled within the `compose` function of each bet subclass.
   *
   * @param outcomeGroups If a number is provided it is considered a binary number and will convert to an array of indices for each bit that is set.
   * Either if it is an array of numbers it is considered to already be an array of bits indices that is set
   * @throws {WagerError} If the outcome selection is invalid.
   * @returns The updated Bet instance.
   */
  addOutcomes(...outcomeGroups: Outcomes | BetEvent): this {
    const [, upperOutcomeBound] = this.productConfig.outcomeBounds;

    const sortedOutcomeGroups = outcomeGroups.map((outcomes) => [
      ...new Set(
        asArray(outcomes).toSorted((a, b) => {
          return a - b;
        })
      ),
    ]);

    for (const outcomes of sortedOutcomeGroups) {
      const [largestSelection = 0] = outcomes.slice(-1);
      const [lowestSelection = 0] = outcomes;

      if (largestSelection > upperOutcomeBound || lowestSelection < 0) {
        throw new WagerError({
          message: `Unsupported selection, exceeds max cap or is negative: [${lowestSelection} ${largestSelection}](${upperOutcomeBound})`,
          clientCode: 'INCOMPLETE_BET',
        });
      }
    }

    this._outcomes.push(sortedOutcomeGroups);

    return this;
  }

  /**
   * Update system number for the bet.
   *
   * @param systemNumber - The system number to place the bet
   * @returns The updated Bet instance.
   */
  setSystemNumber(systemNumber: number): this {
    this._systemNumber = systemNumber;

    return this;
  }

  /**
   * Function to get row Count for board. If system is RSYS or USYS it returns systems rows length witch is equal to row count.
   * If other systemType this function calculates correct row count for board.
   *
   * @returns The given row count for board
   */
  getRowCount(): number {
    if (this.systemNumber) {
      // const system = systems.getSystemFromNumber(this.systemNumber);
      // return system.rows.length;
    }

    return this._outcomes.reduce(
      (sum, outcomeGroup) => outcomeGroup.reduce((rowSum, outcomes) => outcomes.length * (rowSum || 1), 0) * (sum || 1),
      0
    );
  }

  /**
   * Adds a board property to the bet instance, can be for example be a value for how the bet is produces (for PIX).
   *
   * @param boardProperty - The board property to add.
   * @throws {WagerError} If the board property is invalid.
   * @returns The updated Bet instance.
   */
  addBoardProperty(boardProperty: BoardProperty): this {
    this._boardProperties.add(boardProperty);

    return this;
  }

  /**
   * Used for tracking and logs. BoardData is returns in each subClass of board.
   *
   * @param isCompact - If true, returns a more compact format.
   * @returns with systemType and boardProperties.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  toString(isCompact?: boolean): string {
    // Consider make bet hex/binary for a more compact format

    const logEntries = [
      `S:${this.systemType}(${this.systemNumber || this.getRowCount()})`,
      `P:${this.boardProperties.join('|')}`,
    ];

    return logEntries.join(' ');
  }

  /**
   * Used to compose a wager for MSYS and SROW.
   * This is probably the most common ITS wager format
   *
   * @returns composed ITS board data
   */
  abstract compose(): ITSPayloadBoard<object>;

  /**
   * Used to compose a wager for EGNARADER/Externa systemspel that should usually be in a binary or HEX format
   *
   * @returns composed ITS er (egna rader) board data
   */
  abstract composeOwnRows(): ITSPayloadBoard<object>;
}

export default Board;
